import { SafetyTestCategoryCreateRequest } from '../request-models/safety-test-category-create-request.model';
import { SafetyTestCategoryUpdateRequest } from '../request-models/safety-test-category-update-request.model';
import { SafetyTestCreateRequest } from '../request-models/safety-test-create-request.model';
import { SafetyTestRequest } from '../request-models/safety-test-request.model';
import { TestQuestionCreateRequest } from '../request-models/test-question-create-request.model';
import { TestQuestionUpdateRequest } from '../request-models/test-question-update-request.model';
import { omitUndefined } from './wire-value-utils';

/**
 * Builds the PUT /admin/safety-tests/:id (and new-version) body: only the hub's 13 writable fields, and only the
 * ones that are set, so an update stays partial. `null` (clear the field) is kept.
 */
export function toSafetyTestRequest(fields: SafetyTestRequest): SafetyTestRequest {
  return omitUndefined<SafetyTestRequest>({
    categoryId: fields.categoryId,
    title: fields.title,
    shortName: fields.shortName,
    description: fields.description,
    versionNotes: fields.versionNotes,
    effectiveDate: fields.effectiveDate,
    passingScore: fields.passingScore,
    timeLimit: fields.timeLimit,
    questionCount: fields.questionCount,
    shuffleQuestions: fields.shuffleQuestions,
    shuffleOptions: fields.shuffleOptions,
    expirationDays: fields.expirationDays,
    expiringSoonDays: fields.expiringSoonDays,
  });
}

/** Builds the POST /admin/safety-tests body: the writable fields plus the initial questions, when set. */
export function toSafetyTestCreateRequest(fields: SafetyTestCreateRequest): SafetyTestCreateRequest {
  const questions = fields.questions?.map(toTestQuestionCreateRequest);
  return omitUndefined<SafetyTestCreateRequest>({ ...toSafetyTestRequest(fields), title: fields.title, questions });
}

/** Builds a question create body: text, explanation, type and the options' text and correctness. */
export function toTestQuestionCreateRequest(fields: TestQuestionCreateRequest): TestQuestionCreateRequest {
  return omitUndefined<TestQuestionCreateRequest>({
    text: fields.text,
    explanation: fields.explanation,
    type: fields.type,
    options: fields.options.map(option => ({ text: option.text, isCorrect: option.isCorrect })),
  });
}

/** Builds the PUT /admin/safety-tests/:id/questions/:qId body: only text, explanation and type. */
export function toTestQuestionUpdateRequest(fields: TestQuestionUpdateRequest): TestQuestionUpdateRequest {
  return omitUndefined<TestQuestionUpdateRequest>({ text: fields.text, explanation: fields.explanation, type: fields.type });
}

/** Builds the PUT /admin/safety-test-categories/:id body: only the hub's 8 writable fields that are set. */
export function toSafetyTestCategoryRequest(fields: SafetyTestCategoryUpdateRequest): SafetyTestCategoryUpdateRequest {
  return omitUndefined<SafetyTestCategoryUpdateRequest>({
    name: fields.name,
    shortName: fields.shortName,
    description: fields.description,
    icon: fields.icon,
    isRequired: fields.isRequired,
    frequency: fields.frequency,
    frequencyDays: fields.frequencyDays,
    sortOrder: fields.sortOrder,
  });
}

/** Builds the POST /admin/safety-test-categories body: the writable fields plus `organizationId`, when set. */
export function toSafetyTestCategoryCreateRequest(fields: SafetyTestCategoryCreateRequest): SafetyTestCategoryCreateRequest {
  return omitUndefined<SafetyTestCategoryCreateRequest>({
    ...toSafetyTestCategoryRequest(fields),
    name: fields.name,
    shortName: fields.shortName,
    organizationId: fields.organizationId,
  });
}
